import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatTableModule} from '@angular/material/table';
import {MatPaginator, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from "@angular/material/icon";
import {TableLoadingComponent} from "@shared/component/common/table/table-loading/table-loading.component";
import {NoRecordComponent} from "@shared/component/common/table/no-record/no-record.component";
import {BehaviorSubject, catchError, debounceTime, EMPTY, finalize, first, map, Observable, tap} from "rxjs";
import {MatDialog} from "@angular/material/dialog";
import {
  DocumentTemplateFormComponent,
  DocumentTemplateFormComponentData
} from "@modules/document-template/components/document-template-form/document-template-form.component";
import FileService from "@modules/file/services/file.service";
import {DocumentTemplate} from "@modules/document-template/models/document-template.model";
import {ShareDirectivesModule} from "@shared/share-directives/share-directives.module";
import {DocumentTemplateApi} from "@modules/document-template/api/document-template.api";
import {SnackBarService} from "@shared/services/snack-bar.service";
import {ScreenSpinnerService} from "@shared/component/full-screen-spinner/screen-spinner.service";
import {clone} from "lodash-es";
import {LetModule} from "@ngrx/component";
import {
  ViewTemplateComponent,
  ViewTemplateComponentData
} from "@modules/document-template/components/view-template/view-template.component";
import {MatTooltipModule} from "@angular/material/tooltip";
import {Router} from "@angular/router";
import {APP_ROUTES, AppRoutes} from "@config/app-routes.config";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {PaginationData} from "@shared/types/pagination.type";
import {ConnectedPosition, OverlayModule} from "@angular/cdk/overlay";
import {OverlayService} from "@shared/services/overlay.service";
import {TemplatePortal} from "@angular/cdk/portal";
import {HttpClient} from "@angular/common/http";
import {environment} from "@environment/environment";
import {StatusComponent} from "@shared/component/common/status/status.component";

export type DocumentTemplateTableComponentData = {
  templateData: {
    selectedId: string
  },
  hasDocumentTemplateId: string,
  hasDocumentTemplateType: string
  showGenerateDocumentBtn: boolean,
  showLabel: boolean,
  showDownload: boolean,
  showUpdate: boolean
}


@UntilDestroy()
@Component({
  selector: 'app-document-template-table[data]',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatPaginatorModule, ReactiveFormsModule, MatInputModule, MatButtonModule, MatIconModule, TableLoadingComponent, NoRecordComponent, ShareDirectivesModule, LetModule, MatTooltipModule, OverlayModule, StatusComponent],
  templateUrl: './document-template-table.component.html',
  styleUrls: ['./document-template-table.component.scss'],
})
export class DocumentTemplateTableComponent implements OnInit, OnChanges {
  @ViewChild('triggerEl', {read: ElementRef}) triggerEl!: ElementRef<HTMLElement>;
  @ViewChild('infoTpl') infoTpl!: TemplateRef<any>;
  @Output() useTemplate = new EventEmitter<DocumentTemplate>()
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() data: DocumentTemplateTableComponentData
  @Input() isActive = true;

  form = this.fb.group({
    searchText: this.fb.control(''),
  });

  filterAndSortInput: {
    searchText: string,
  } = {
    searchText: '',
  };

  displayedColumns: string[] = ['name', 'status', 'frequencyOfGeneration', 'updatedAt', 'actions'];
  _data$ = new BehaviorSubject<PaginationData<DocumentTemplate>>(null);
  _loading$ = new BehaviorSubject(false);


  private readonly _items$ = new BehaviorSubject<DocumentTemplate[]>([]);

  get data$() {
    return this._data$.asObservable()
  }

  get items$() {
    return this._items$.asObservable()
  }

  private get apiUrl() {
    const baseUrl = new URL(environment.apiUrl).origin

    return `${baseUrl}`
  }

  readonly loading$ = this._loading$.asObservable()
  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -4,
    },
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 4,
    },
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -4,
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetX: 4,
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetX: -4,
      offsetY: 4,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetX: 4,
      offsetY: -4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetX: -4,
      offsetY: -4,
    }
  ];

  constructor(
    private fb: FormBuilder,
    private matDialog: MatDialog,
    private fileService: FileService,
    private documentTemplateApi: DocumentTemplateApi,
    private snackBarService: SnackBarService,
    private screenSpinnerService: ScreenSpinnerService,
    private router: Router,
    @Inject(APP_ROUTES) public appRoutes: AppRoutes,
    private overlayService: OverlayService,
    private httpClient: HttpClient,
    private viewContainerRef: ViewContainerRef,
  ) {
  }

  create() {
    const {hasDocumentTemplateId, hasDocumentTemplateType} = this.data

    const ref = this.matDialog.open<
      DocumentTemplateFormComponent,
      DocumentTemplateFormComponentData,
      DocumentTemplate | null
    >(DocumentTemplateFormComponent, {
      width: DocumentTemplateFormComponent.width,
      autoFocus: false,
      data: {
        hasDocumentTemplateId,
        hasDocumentTemplateType
      } as DocumentTemplateFormComponentData
    })

    ref.afterClosed().pipe(
      tap(res => this._items$.next([res, ...this._items$.value].filter(v => !!v)))
    ).subscribe()
  }

  update(document: DocumentTemplate) {
    const {hasDocumentTemplateId, hasDocumentTemplateType} = this.data

    const ref = this.matDialog.open<
      DocumentTemplateFormComponent,
      DocumentTemplateFormComponentData,
      DocumentTemplate | null
    >(DocumentTemplateFormComponent, {
      width: DocumentTemplateFormComponent.width,
      autoFocus: false,
      data: {
        id: document.id,
        name: document.name,
        hasDocumentTemplateId,
        hasDocumentTemplateType
      } as DocumentTemplateFormComponentData
    })

    ref.afterClosed().pipe(
      tap(updatedDocument => {
        if (updatedDocument) {
          const newData = clone(this._items$.value)

          const index = newData.findIndex(i => i.id === updatedDocument.id)

          if (index > -1) {
            newData[index] = updatedDocument
          }

          this._items$.next(newData)
        }
      })
    ).subscribe()
  }

  downloadFile(id: string): void {
    this.fileService.downloadFile(id)
  }

  ngOnInit(): void {
    if (this.isActive) {
      this.getDocumentTemplates()
    }

    this.data$.pipe(
      untilDestroyed(this),
      tap(paginationData => {
        paginationData?.data ? this.setItems([...paginationData.data]) : this.setItems([]);
      }),
    ).subscribe();

    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(1000),
        tap(() => this.applyFilter()),
      ).subscribe();

    this.loading$.pipe(
      untilDestroyed(this),
      tap(value => {
        const control = this.form.controls.searchText

        value ? control.disable({emitEvent: false}) : control.enable({emitEvent: false})
      })
    ).subscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isActiveChange = changes['isActive'];
    const dataChange = changes['data'];

    const becameActive = !!isActiveChange?.currentValue
      && !isActiveChange.firstChange
      && isActiveChange.previousValue !== isActiveChange.currentValue;

    const entityChanged = !!dataChange?.currentValue
      && !dataChange.firstChange
      && (
        dataChange.previousValue?.hasDocumentTemplateId !== dataChange.currentValue?.hasDocumentTemplateId
        || dataChange.previousValue?.hasDocumentTemplateType !== dataChange.currentValue?.hasDocumentTemplateType
      );

    if (!this.isActive) {
      return;
    }

    if (entityChanged) {
      this.filterAndSortInput.searchText = '';
      this.form.patchValue({
        searchText: '',
      }, {
        emitEvent: false,
      });

      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }

      this.getDocumentTemplates();

      return;
    }

    if (becameActive) {
      this.getDocumentTemplates();
    }
  }

  setItems(items: DocumentTemplate[]) {
    this._items$.next(items);
  }

  applyFilter() {
    this.filterAndSortInput.searchText = this.form.value.searchText?.trim()?.toLowerCase();

    this.paginator?.firstPage();

    this.getDocumentTemplates();
  }

  onPageChanged($event: PageEvent) {
    this.getDocumentTemplates();
  }

  getDocumentTemplates$(): Observable<PaginationData<DocumentTemplate>> {
    this._loading$.next(true)

    return this.documentTemplateApi.get$({
        pagination: {
          perPage: this.paginator?.pageSize ?? 10,
          currentPage: this.paginator?.pageIndex ? this.paginator.pageIndex + 1 : 1,
        },
        searchText: this.filterAndSortInput.searchText,
        hasDocumentTemplateId: this.data.hasDocumentTemplateId
      },
    ).pipe(
      tap(paginationData => {
        this._data$.next(paginationData);

        this._loading$.next(false)
      }),
      catchError(() => {
        this._loading$.next(false)

        return EMPTY
      })
    );
  }

  getDocumentTemplates(): void {
    this._data$.next(null)

    this.getDocumentTemplates$().pipe(first()).subscribe();
  }

  delete(element: DocumentTemplate) {
    this.screenSpinnerService.loading()

    this.documentTemplateApi.delete$(element.id).pipe(
      tap(res => {
        this.screenSpinnerService.stopLoading()

        if (res) {
          this.snackBarService.pushItemDeletedMessage()

          this.removeItem(element)

          return
        }

        this.snackBarService.pushErrorMessage()
      }),
      catchError(e => {
        this.screenSpinnerService.stopLoading()

        this.snackBarService.pushErrorMessage()

        return e
      })
    ).subscribe()
  }

  view(template: DocumentTemplate, withHighlight = false) {
    this.screenSpinnerService.loading()

    const $url: Observable<string> = !withHighlight
      ? this.fileService.downloadFile$(template.template.id)
      : this.httpClient.get(`${this.apiUrl}/document-template/${template.id}/review`, {
        withCredentials: true,
      }).pipe(map(res => (res as any).url))

    $url.pipe(
      tap(url => {
        this.matDialog.open<ViewTemplateComponent, ViewTemplateComponentData>(ViewTemplateComponent, {
          ...ViewTemplateComponent.config,
          data: {
            url,
            template,
          }
        })

        this.screenSpinnerService.stopLoading()
      }),
      catchError(e => {
        this.screenSpinnerService.stopLoading()

        this.snackBarService.pushErrorMessage()

        return e
      })
    ).subscribe()
  }

  download(item: DocumentTemplate) {
    return this.downloadFile(item.template.id)
  }

  removeItem(item: DocumentTemplate) {
    const values = clone(this._items$.value)

    const index = values.findIndex(i => i.id === item.id)

    if (index > -1) {
      values.splice(index, 1)

      this._items$.next([...values])
    }
  }

  generateDocument(template: DocumentTemplate) {
    if (this.data.showGenerateDocumentBtn && this.data.templateData.selectedId) {
      this.generate$({
        templateId: template.id,
        selectedId: this.data.templateData.selectedId
      }).subscribe()
    }
  }

  generateAllTemplates() {
    if (this.data.showGenerateDocumentBtn && this.data.templateData.selectedId) {
      this.generate$({
        hasDocumentTemplateId: this.data.hasDocumentTemplateId,
        selectedId: this.data.templateData.selectedId
      }).subscribe()
    }
  }

  generate$(data) {
    return this.httpClient.post(`${this.apiUrl}/document-template/generate`, {
      data
    }, {
      withCredentials: true,
    }).pipe(
      this.screenSpinnerService.withSpinner(),
      tap((data: any) => this.handleResponse(data)),
      catchError((e) => {
        this.snackBarService.pushErrorAlert('The templates cannot be generated because the data is missing.')

        return EMPTY
      }),
    )
  }

  resetForm($event: MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }

    this.form.reset({
      searchText: '',
    });
  }

  onMouseEnter(event: MouseEvent) {
    const originEl = this.triggerEl.nativeElement;
    this.overlayService.init(originEl, this.overlayPositions);
    const portal = new TemplatePortal(this.infoTpl, this.viewContainerRef!);
    this.overlayService.open(portal);
  }

  onMouseLeave() {
    this.overlayService.close();
  }

  handleResponse(data: any) {
    if (typeof data === 'string') {
      window.open(data, '_blank').focus()
    }

    // if (typeof data === 'object') {
    //   this.snackBarService.pushAlert(data.message as string)
    // }
  }
}
